/**
 * Universal route-transition skeleton — DESIGN_SYSTEM.md §1.3 / §9 (loading states).
 * Premium minimal: subtle gray fills, slow white sheen, soft breath motion, rounded-card / 16px.
 */

import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@hooks/useLanguage';
import {
  SkeletonBreath,
  SkeletonRect,
  SkeletonText,
} from './skeletons/skeletonPrimitives';

export type PageSkeletonVariant = 'default' | 'auth';

export interface PageSkeletonProps {
  /** `default`: dashboard-style header + table outline. `auth`: centered card blocks. */
  variant?: PageSkeletonVariant;
  className?: string;
}

const cardShell = 'rounded-card border border-slate-200/50 bg-white';
const rowShell =
  'flex flex-col gap-2 rounded-[16px] border border-slate-100/70 bg-slate-50/40 py-3 ps-4 pe-4 sm:flex-row sm:items-center sm:gap-4';

export function PageSkeleton({ variant = 'default', className = '' }: PageSkeletonProps) {
  const { t } = useTranslation();
  const { isRtl } = useLanguage();
  const isAuth = variant === 'auth';

  return (
    <div
      className={clsx('font-sans animate-pulse', className)}
      dir={isRtl ? 'rtl' : 'ltr'}
      aria-busy="true"
      aria-live="polite"
      aria-label={t('common.loading', { defaultValue: 'Loading…' })}
    >
      <SkeletonBreath>
        {isAuth ? (
          <div className="flex w-full min-h-[min(60vh,28rem)] flex-col items-center justify-center px-4 py-10">
            <div className={clsx(cardShell, 'w-full max-w-md space-y-4 p-8')}>
              <SkeletonRect className="mx-auto h-8 w-3/5 max-w-[200px]" />
              <SkeletonText className="w-full max-w-[280px]" />
              <SkeletonText className="w-4/5 max-w-[240px]" />
              <div className="space-y-3 pt-4">
                <SkeletonRect className="h-12 w-full" />
                <SkeletonRect className="h-12 w-full" />
              </div>
              <SkeletonRect className="mx-auto mt-6 h-11 w-full max-w-[200px]" />
            </div>
          </div>
        ) : (
          <div className="flex h-full min-h-0 w-full flex-col gap-4">
            <section className={clsx(cardShell, 'p-4 sm:p-6')}>
              <SkeletonText className="w-[min(40%,280px)]" />
              <SkeletonText className="mt-3 w-[min(55%,360px)] max-w-full" />
            </section>
            <section className={clsx(cardShell, 'min-h-[200px] flex-1 p-4 sm:p-6')}>
              <div className="mb-4 hidden gap-3 border-b border-slate-100 pb-3 sm:flex sm:flex-row sm:items-center">
                <SkeletonText className="min-w-0 flex-[1.2]" />
                <SkeletonText className="min-w-0 flex-1" />
                <SkeletonText className="w-20 shrink-0 sm:ms-auto" />
                <SkeletonText className="w-24 shrink-0" />
              </div>
              <div className="space-y-3">
                {Array.from({ length: 6 }, (_, i) => (
                  <div key={i} className={rowShell}>
                    <SkeletonText className="min-w-[100px] flex-1 sm:max-w-[30%]" />
                    <SkeletonText className="min-w-[80px] flex-1 sm:max-w-[24%]" />
                    <SkeletonText className="h-2.5 w-20 shrink-0 rounded-full sm:ms-auto" />
                    <SkeletonText className="w-24 shrink-0" />
                  </div>
                ))}
              </div>
              <div className="mt-6 flex justify-center gap-2 sm:justify-end">
                <SkeletonRect className="h-9 w-24" />
                <SkeletonRect className="h-9 w-24" />
              </div>
            </section>
          </div>
        )}
      </SkeletonBreath>
    </div>
  );
}
